import { Back } from "../ui/Icons";
import { Line } from "../ui/Skeleton";
import { SEARCH_PARTS, SEO_TABS, firstTab, type SeoTab } from "./tabs/tabs";

/** The search and AI page, before it has arrived: the bar, the tab row with
 *  the tab it will open on lit, and that tab's first things in their places
 *  (the overview's tiles, or the AI and Search tabs' row of buttons, or the
 *  settings' first card), since a reload as often opens on one of the others. The same reasoning as ui/Shell.tsx, and the same flat bar, drawn
 *  here because that one is not exported. Kept beside the page so the two
 *  change together: what is drawn here is what StudioSeo draws while it reads.
 *  And drawn a third time, by hand, as #shell-seo in index.html, which stands
 *  on the screen before any of this has downloaded: anything changed here
 *  changes there too. */
export function SeoShell() {
  const tab = firstTab(window.location.search);
  return (
    <>
      <header className="appbar appbar-wide">
        <div className="appbar-inner">
          <span className="iconbtn" aria-hidden="true">
            <Back />
          </span>
          <div className="appbar-title grow">Search &amp; AI</div>
        </div>
      </header>
      <div className="wrap wrap-wide" aria-hidden="true">
        <div className="chiprow seo-tabs">
          {SEO_TABS.map(({ key, label }) => (
            <span className={`chip${key === tab ? " chip-on" : ""}`} key={key}>
              {label}
            </span>
          ))}
        </div>
        <SeoSkeleton tab={tab} />
      </div>
    </>
  );
}

/** Also what the page shows under its tab row while its documents are read,
 *  in the open tab's shape. The parts are drawn or not by CSS off data-tab, as
 *  #shell-seo in index.html does it, so the two stay the same markup. */
export function SeoSkeleton({ tab }: { tab: SeoTab }) {
  return (
    <div className="seo-skeleton" data-tab={tab} aria-hidden="true">
      <div className="stats-tiles seo-sk-tiles">
        {[0, 1, 2, 3].map((i) => (
          <div className="stat stack" key={i}>
            <Line w="50%" h={11} />
            <Line w="40%" h={24} />
            <Line w="70%" h={11} />
          </div>
        ))}
      </div>
      <div className="seo-sk-actions">
        <div className="row" style={{ gap: 8 }}>
          <div className="skeleton" style={{ width: 150, height: 40, borderRadius: 999 }} />
          <div className="skeleton" style={{ width: 120, height: 40, borderRadius: 999 }} />
        </div>
        <Line w="85%" h={11} />
        <Line w="60%" h={11} />
      </div>
      <div className="chiprow chiprow-tight seo-parts seo-sk-parts">
        {SEARCH_PARTS.map((p, i) => (
          <span className={`chip chip-sm${i === 0 ? " chip-on" : ""}`} key={p.key}>
            {p.label}
          </span>
        ))}
      </div>
      <div className="card stack">
        <Line w="36%" h={18} />
        <Line w="70%" h={11} />
        {[0, 1, 2].map((i) => (
          <div className="stack" key={i}>
            <Line w={`${48 + i * 9}%`} />
            <div className="skeleton" style={{ height: 8, borderRadius: 999 }} />
          </div>
        ))}
        <div className="skeleton" style={{ height: 150, borderRadius: 12 }} />
      </div>
    </div>
  );
}
